import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  AlertTriangle,
  Shield,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Check,
  ChevronRight,
  ChevronLeft,
  Info,
  Loader2,
  FileText,
  Scale,
  AlertCircle,
  ExternalLink
} from 'lucide-react'
import { InvestmentsAPI, AccountsAPI, extractArray } from '../services/api'
import SwiftGoldCaptcha from './SwiftGoldCaptcha'

const STEPS = {
  SELECT_SECURITY: 0,
  SELECT_ACCOUNT: 1,
  RISK_DISCLOSURE: 2,
  ORDER_DETAILS: 3,
  REVIEW_CONFIRM: 4
}

const RISK_LEVELS = {
  low: { color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Low Risk', icon: Shield },
  medium: { color: 'text-yellow-600 bg-yellow-50 border-yellow-200', label: 'Medium Risk', icon: AlertTriangle },
  high: { color: 'text-orange-600 bg-orange-50 border-orange-200', label: 'High Risk', icon: AlertTriangle },
  'very high': { color: 'text-red-600 bg-red-50 border-red-200', label: 'Very High Risk', icon: AlertCircle }
}

export default function InvestmentModal({ isOpen, onClose, security, securities = [], onSuccess }) {
  const [currentStep, setCurrentStep] = useState(security ? STEPS.SELECT_ACCOUNT : STEPS.SELECT_SECURITY)
  const [loading, setLoading] = useState(false)
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const [loadingSecurities, setLoadingSecurities] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Security selection
  const [availableSecurities, setAvailableSecurities] = useState(securities)
  const [selectedSecurity, setSelectedSecurity] = useState(security)
  const [securitySearch, setSecuritySearch] = useState('')

  // Form state
  const [investmentAccounts, setInvestmentAccounts] = useState([])
  const [bankAccounts, setBankAccounts] = useState([])
  const [selectedInvestmentAccount, setSelectedInvestmentAccount] = useState(null)
  const [selectedBankAccount, setSelectedBankAccount] = useState(null)

  // Risk acceptance
  const [riskAccepted, setRiskAccepted] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [suitabilityConfirmed, setSuitabilityConfirmed] = useState(false)
  const [lossAcknowledged, setLossAcknowledged] = useState(false)

  // Order details
  const [orderType, setOrderType] = useState('market')
  const [investmentAmount, setInvestmentAmount] = useState('')
  const [limitPrice, setLimitPrice] = useState('')

  // Captcha
  const [showCaptcha, setShowCaptcha] = useState(false)
  const [captchaVerified, setCaptchaVerified] = useState(false)

  useEffect(() => {
    if (isOpen) {
      if (security) {
        setSelectedSecurity(security)
        setCurrentStep(STEPS.SELECT_ACCOUNT)
      } else {
        setCurrentStep(STEPS.SELECT_SECURITY)
        fetchSecurities()
      }
      fetchAccounts()
      resetForm()
    }
  }, [isOpen, security])

  const fetchSecurities = async () => {
    if (securities.length > 0) {
      setAvailableSecurities(securities)
      return
    }
    setLoadingSecurities(true)
    try {
      const res = await InvestmentsAPI.getSecurities()
      if (res.success) {
        setAvailableSecurities(extractArray(res))
      }
    } catch (err) {
      console.error('Error fetching securities:', err)
    } finally {
      setLoadingSecurities(false)
    }
  }

  const resetForm = () => {
    setSelectedSecurity(security || null)
    setSecuritySearch('')
    setSelectedInvestmentAccount(null)
    setSelectedBankAccount(null)
    setRiskAccepted(false)
    setTermsAccepted(false)
    setSuitabilityConfirmed(false)
    setLossAcknowledged(false)
    setOrderType('market')
    setInvestmentAmount('')
    setLimitPrice('')
    setError(null)
    setSuccess(false)
    setShowCaptcha(false)
    setCaptchaVerified(false)
  }

  const fetchAccounts = async () => {
    setLoadingAccounts(true)
    try {
      const [invRes, bankRes] = await Promise.all([
        InvestmentsAPI.getAccounts(),
        AccountsAPI.getAll()
      ])

      // Investment accounts come from dedicated investment_accounts table
      if (invRes.success) {
        const investmentAccts = extractArray(invRes).filter(a => a.status === 'active')
        setInvestmentAccounts(investmentAccts)
      }

      // Bank accounts for funding (checking/savings)
      if (bankRes.success) {
        const allAccounts = extractArray(bankRes).filter(a => a.status === 'active' || !a.status)
        setBankAccounts(allAccounts)
      }
    } catch (err) {
      console.error('Error fetching accounts:', err)
      setError('Error al cargar las cuentas')
    } finally {
      setLoadingAccounts(false)
    }
  }

  // Use selectedSecurity for calculations
  const activeSecurity = selectedSecurity
  const currentPrice = parseFloat(activeSecurity?.current_price) || 0
  const priceForCalc = orderType === 'limit' ? (parseFloat(limitPrice) || currentPrice) : currentPrice
  const estimatedShares = investmentAmount ? parseFloat(investmentAmount) / priceForCalc : 0
  const estimatedCost = parseFloat(investmentAmount) || 0

  // Filtered securities for selection
  const filteredSecurities = availableSecurities.filter(s => {
    if (!securitySearch) return true
    const search = securitySearch.toLowerCase()
    return s.symbol?.toLowerCase().includes(search) || s.name?.toLowerCase().includes(search)
  })

  const canProceedToNext = () => {
    switch (currentStep) {
      case STEPS.SELECT_SECURITY:
        return !!selectedSecurity
      case STEPS.SELECT_ACCOUNT:
        return selectedInvestmentAccount && selectedBankAccount
      case STEPS.RISK_DISCLOSURE:
        return riskAccepted && termsAccepted && suitabilityConfirmed && lossAcknowledged
      case STEPS.ORDER_DETAILS:
        const amount = parseFloat(investmentAmount) || 0
        const balance = parseFloat(selectedBankAccount?.balance) || 0
        return amount > 0 && amount <= balance
      case STEPS.REVIEW_CONFIRM:
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    if (currentStep < STEPS.REVIEW_CONFIRM) {
      setCurrentStep(currentStep + 1)
      setError(null)
    }
  }

  const handleBack = () => {
    const minStep = security ? STEPS.SELECT_ACCOUNT : STEPS.SELECT_SECURITY
    if (currentStep > minStep) {
      setCurrentStep(currentStep - 1)
      setError(null)
    }
  }

  const handleSubmitOrder = async (skipCaptchaCheck = false) => {
    // Mostrar captcha si no ha sido verificado
    if (!skipCaptchaCheck && !captchaVerified) {
      setShowCaptcha(true)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const finalQuantity = estimatedShares

      const res = await InvestmentsAPI.placeOrder(
        selectedInvestmentAccount.id,
        selectedSecurity.id,
        orderType,
        'buy',
        finalQuantity,
        orderType === 'limit' ? parseFloat(limitPrice) : null,
        null,
        selectedBankAccount?.id // fundingAccountId
      )

      if (res.success) {
        setSuccess(true)
        setTimeout(() => {
          onSuccess && onSuccess(res.data)
          onClose()
        }, 2000)
      } else {
        setError(res.message || 'Error al procesar la orden')
      }
    } catch (err) {
      setError(err.message || 'Ocurrio un error al procesar su orden')
    } finally {
      setLoading(false)
    }
  }

  const handleCaptchaVerified = () => {
    setCaptchaVerified(true)
    setShowCaptcha(false)
    // Auto-submit después de verificar, pasando true para saltar la verificación del captcha
    // ya que sabemos que acabamos de verificarlo
    handleSubmitOrder(true)
  }

  const handleCaptchaFailed = () => {
    setError('Verificacion fallida. Por favor intente de nuevo.')
    setShowCaptcha(false)
  }

  if (!isOpen) return null

  const riskLevel = RISK_LEVELS[selectedSecurity?.risk_level?.toLowerCase()] || RISK_LEVELS.medium
  const RiskIcon = riskLevel.icon

  // Success screen
  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-black/50"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center"
        >
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Order Submitted!</h2>
          <p className="text-slate-600 mb-4">
            Your order to buy {estimatedShares.toFixed(4)} shares of {selectedSecurity?.symbol} has been submitted successfully.
          </p>
          <p className="text-sm text-slate-500">Redirecting...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <AnimatePresence>
      {/* Captcha Modal */}
      {showCaptcha && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowCaptcha(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <SwiftGoldCaptcha
              onVerified={handleCaptchaVerified}
              onFailed={handleCaptchaFailed}
            />
          </motion.div>
        </motion.div>
      )}

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div className="flex items-center gap-4">
              {selectedSecurity ? (
                <>
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <span className="text-slate-900 font-bold">{selectedSecurity.symbol?.charAt(0)}</span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Invest in {selectedSecurity.symbol}</h2>
                    <p className="text-sm text-slate-500">{selectedSecurity.name}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">New Investment</h2>
                    <p className="text-sm text-slate-500">Select a security to invest in</p>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center justify-between">
              {(security
                ? ['Account', 'Risk Disclosure', 'Order', 'Confirm']
                : ['Security', 'Account', 'Risk Disclosure', 'Order', 'Confirm']
              ).map((step, index) => {
                const stepIndex = security ? index + 1 : index
                return (
                  <div key={step} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      stepIndex < currentStep
                        ? 'bg-emerald-500 text-white'
                        : stepIndex === currentStep
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-200 text-slate-500'
                    }`}>
                      {stepIndex < currentStep ? <Check className="w-4 h-4" /> : index + 1}
                    </div>
                    <span className={`ml-2 text-xs font-medium hidden sm:block ${
                      stepIndex <= currentStep ? 'text-slate-900' : 'text-slate-400'
                    }`}>
                      {step}
                    </span>
                    {index < (security ? 3 : 4) && (
                      <ChevronRight className="w-4 h-4 text-slate-300 mx-2 hidden sm:block" />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Step 0: Select Security (only if no security provided) */}
            {currentStep === STEPS.SELECT_SECURITY && (
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    value={securitySearch}
                    onChange={(e) => setSecuritySearch(e.target.value)}
                    placeholder="Search by symbol or name..."
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {loadingSecurities ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                  </div>
                ) : filteredSecurities.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No securities found</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {filteredSecurities.map(sec => {
                      const price = parseFloat(sec.current_price || sec.price) || 0
                      const changePercent = parseFloat(sec.change_percent || sec.price_change_percent || sec.changePercent) || 0
                      const secRisk = RISK_LEVELS[sec.risk_level?.toLowerCase()] || RISK_LEVELS.medium
                      return (
                        <button
                          key={sec.id}
                          onClick={() => setSelectedSecurity(sec)}
                          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                            selectedSecurity?.id === sec.id
                              ? 'border-slate-900 bg-slate-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                <span className="text-slate-900 font-bold text-sm">{sec.symbol?.charAt(0)}</span>
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">{sec.symbol}</p>
                                <p className="text-xs text-slate-500">{sec.name}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-slate-900">${price.toFixed(2)}</p>
                              <p className={`text-xs ${changePercent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-medium capitalize">
                              {sec.security_type || sec.type || 'Stock'}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${secRisk.color}`}>
                              {secRisk.label}
                            </span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Select Account */}
            {currentStep === STEPS.SELECT_ACCOUNT && (
              <div className="space-y-6">
                {loadingAccounts ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Investment Account</h3>
                      {investmentAccounts.length === 0 ? (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                            <div>
                              <p className="text-yellow-800 text-sm font-medium">No Investment Account</p>
                              <p className="text-yellow-700 text-xs mt-1">You need to open an investment account before you can invest. Please visit the Investment Center to open an account.</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {investmentAccounts.map(account => (
                            <button
                              key={account.id}
                              onClick={() => setSelectedInvestmentAccount(account)}
                              className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                                selectedInvestmentAccount?.id === account.id
                                  ? 'border-slate-900 bg-slate-50'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium text-slate-900">{account.account_name || 'Investment Account'}</p>
                                  <p className="text-sm text-slate-500">{account.account_number}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-slate-900">${parseFloat(account.cash_balance || 0).toLocaleString()}</p>
                                  <p className="text-xs text-slate-500">Cash Available</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Funding Source</h3>
                      {bankAccounts.length === 0 ? (
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                          <p className="text-slate-600 text-sm">No bank accounts available for funding.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {bankAccounts.map(account => (
                            <button
                              key={account.id}
                              onClick={() => setSelectedBankAccount(account)}
                              className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                                selectedBankAccount?.id === account.id
                                  ? 'border-slate-900 bg-slate-50'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium text-slate-900">{account.account_name || account.name}</p>
                                  <p className="text-sm text-slate-500">****{account.account_number?.slice(-4)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-slate-900">${parseFloat(account.balance || 0).toLocaleString()}</p>
                                  <p className="text-xs text-slate-500">Available</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2: Risk Disclosure */}
            {currentStep === STEPS.RISK_DISCLOSURE && (
              <div className="space-y-6">
                {/* Security Risk Level */}
                <div className={`p-4 rounded-lg border ${riskLevel.color}`}>
                  <div className="flex items-center gap-3">
                    <RiskIcon className="w-5 h-5" />
                    <span className="font-semibold">{riskLevel.label} Investment</span>
                  </div>
                  <p className="mt-2 text-sm opacity-80">
                    {selectedSecurity?.symbol} is classified as a {selectedSecurity?.risk_level?.toLowerCase() || 'medium'} risk investment based on its historical volatility and market characteristics.
                  </p>
                </div>

                {/* Risk Disclosure Document */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Investment Risk Disclosure</h4>
                      <div className="text-sm text-slate-600 space-y-3">
                        <p><strong>Market Risk:</strong> The value of your investment can go down as well as up. You may receive back less than you originally invested.</p>
                        <p><strong>Volatility:</strong> Stock prices can be volatile and may fluctuate significantly over short periods of time.</p>
                        <p><strong>Liquidity Risk:</strong> In certain market conditions, you may not be able to sell your investment quickly or at a favorable price.</p>
                        <p><strong>No Guarantees:</strong> Past performance is not a guarantee of future results. There is no guarantee that this investment will achieve its objectives.</p>
                        <p><strong>Capital at Risk:</strong> You could lose some or all of your invested capital.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Regulatory Notice */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Scale className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Regulatory Notice</h4>
                      <p className="text-sm text-blue-800 mb-2">
                        Securities investments are <strong>NOT</strong> insured by the FDIC. They are <strong>NOT</strong> deposits or obligations of any bank. They are <strong>NOT</strong> guaranteed by any bank. They are subject to investment risks, including possible loss of principal.
                      </p>
                      <p className="text-sm text-blue-800">
                        Swift Gold Fiduciary Securities is a member of FINRA/SIPC. SIPC protects against the loss of cash and securities held by a customer at a financially-troubled SIPC-member brokerage firm, up to $500,000 (including $250,000 for cash).
                      </p>
                    </div>
                  </div>
                </div>

                {/* Acknowledgments */}
                <div className="space-y-2 bg-white border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Please confirm the following:</h4>

                  <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={riskAccepted}
                      onChange={(e) => setRiskAccepted(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                    <span className="text-sm text-slate-700">
                      I have read and understand the <strong>Investment Risk Disclosure</strong> above, including the risks associated with investing in {selectedSecurity?.symbol}.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={lossAcknowledged}
                      onChange={(e) => setLossAcknowledged(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                    <span className="text-sm text-slate-700">
                      I acknowledge that I <strong>may lose some or all</strong> of my invested capital and that past performance does not guarantee future results.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={suitabilityConfirmed}
                      onChange={(e) => setSuitabilityConfirmed(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                    <span className="text-sm text-slate-700">
                      I confirm that this investment is <strong>suitable for my financial situation</strong>, investment objectives, and risk tolerance.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                    <span className="text-sm text-slate-700">
                      I have read and agree to the{' '}
                      <a href="#" className="text-blue-600 underline hover:text-blue-800">Terms of Service</a>,{' '}
                      <a href="#" className="text-blue-600 underline hover:text-blue-800">Brokerage Agreement</a>, and{' '}
                      <a href="#" className="text-blue-600 underline hover:text-blue-800">Privacy Policy</a>.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Step 3: Order Details */}
            {currentStep === STEPS.ORDER_DETAILS && (
              <div className="space-y-6">
                {/* Current Price */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm text-slate-600">Current Market Price</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl font-bold text-slate-900">${currentPrice.toFixed(2)}</span>
                        <span className={`text-sm font-medium flex items-center ${
                          parseFloat(selectedSecurity?.change_percent || selectedSecurity?.price_change_percent || 0) >= 0
                            ? 'text-emerald-600'
                            : 'text-red-600'
                        }`}>
                          {parseFloat(selectedSecurity?.change_percent || selectedSecurity?.price_change_percent || 0) >= 0
                            ? <TrendingUp className="w-4 h-4 mr-1" />
                            : <TrendingDown className="w-4 h-4 mr-1" />
                          }
                          {parseFloat(selectedSecurity?.change_percent || selectedSecurity?.price_change_percent || 0).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-slate-500">Risk Level</span>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium mt-1 ${riskLevel.color}`}>
                        {riskLevel.label}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Type */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Order Type</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setOrderType('market')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        orderType === 'market'
                          ? 'border-slate-900 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="font-medium text-slate-900">Market Order</p>
                      <p className="text-xs text-slate-500 mt-1">Buy at current market price</p>
                    </button>
                    <button
                      onClick={() => setOrderType('limit')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        orderType === 'limit'
                          ? 'border-slate-900 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="font-medium text-slate-900">Limit Order</p>
                      <p className="text-xs text-slate-500 mt-1">Set your maximum price</p>
                    </button>
                  </div>
                </div>

                {orderType === 'limit' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Limit Price</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="number"
                        value={limitPrice}
                        onChange={(e) => setLimitPrice(e.target.value)}
                        placeholder={currentPrice.toFixed(2)}
                        step="0.01"
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Order will only execute at this price or better</p>
                  </div>
                )}

                {/* Investment Amount */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Investment Amount</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="number"
                      value={investmentAmount}
                      onChange={(e) => setInvestmentAmount(e.target.value)}
                      placeholder="Enter amount to invest"
                      min="0"
                      step="0.01"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-lg"
                    />
                  </div>
                  {investmentAmount && (
                    <p className="text-sm text-slate-600 mt-2">
                      ≈ <strong>{estimatedShares.toFixed(4)}</strong> shares of {selectedSecurity?.symbol}
                    </p>
                  )}
                </div>

                {/* Quick Amount Buttons */}
                <div className="flex gap-2">
                  {[100, 250, 500, 1000, 2500].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setInvestmentAmount(amount.toString())}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        investmentAmount === amount.toString()
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }`}
                    >
                      ${amount >= 1000 ? `${amount/1000}k` : amount}
                    </button>
                  ))}
                </div>

                {/* Available Balance */}
                <div className="p-4 bg-slate-50 rounded-lg flex justify-between items-center">
                  <span className="text-sm text-slate-600">Available in {selectedBankAccount?.account_name}</span>
                  <span className="font-semibold text-slate-900">${parseFloat(selectedBankAccount?.balance || 0).toLocaleString()}</span>
                </div>

                {parseFloat(investmentAmount) > parseFloat(selectedBankAccount?.balance || 0) && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Insufficient funds. Please enter a smaller amount.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review & Confirm */}
            {currentStep === STEPS.REVIEW_CONFIRM && (
              <div className="space-y-6">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-emerald-900">Ready to Submit</h4>
                      <p className="text-sm text-emerald-800">Please review your order details before confirming.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                  <div className="p-4 flex justify-between">
                    <span className="text-slate-600">Security</span>
                    <span className="font-semibold text-slate-900">{selectedSecurity?.symbol} - {selectedSecurity?.name}</span>
                  </div>
                  <div className="p-4 flex justify-between">
                    <span className="text-slate-600">Order Type</span>
                    <span className="font-semibold text-slate-900 capitalize">{orderType} Order</span>
                  </div>
                  <div className="p-4 flex justify-between">
                    <span className="text-slate-600">Action</span>
                    <span className="font-semibold text-emerald-600">Buy</span>
                  </div>
                  <div className="p-4 flex justify-between">
                    <span className="text-slate-600">Price</span>
                    <span className="font-semibold text-slate-900">
                      ${priceForCalc.toFixed(2)}
                      {orderType === 'market' && <span className="text-xs text-slate-500 ml-1">(market)</span>}
                    </span>
                  </div>
                  <div className="p-4 flex justify-between">
                    <span className="text-slate-600">Estimated Shares</span>
                    <span className="font-semibold text-slate-900">{estimatedShares.toFixed(4)}</span>
                  </div>
                  <div className="p-4 flex justify-between">
                    <span className="text-slate-600">Investment Account</span>
                    <span className="font-semibold text-slate-900">{selectedInvestmentAccount?.account_name || 'Investment Account'}</span>
                  </div>
                  <div className="p-4 flex justify-between">
                    <span className="text-slate-600">Funding Source</span>
                    <span className="font-semibold text-slate-900">{selectedBankAccount?.account_name}</span>
                  </div>
                  <div className="p-4 flex justify-between bg-slate-50">
                    <span className="font-semibold text-slate-900">Total Amount</span>
                    <span className="text-xl font-bold text-slate-900">${estimatedCost.toFixed(2)}</span>
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Important Notice</p>
                      <p>By clicking "Confirm Order", you authorize Swift Gold Fiduciary to execute this trade on your behalf. Market orders execute at the best available price, which may differ from the displayed price. Your investment account will be debited accordingly.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50">
            <button
              onClick={() => {
                const minStep = security ? STEPS.SELECT_ACCOUNT : STEPS.SELECT_SECURITY
                if (currentStep === minStep) {
                  onClose()
                } else {
                  handleBack()
                }
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-slate-600 hover:text-slate-900 font-medium transition-colors"
            >
              {currentStep > (security ? STEPS.SELECT_ACCOUNT : STEPS.SELECT_SECURITY) && <ChevronLeft className="w-4 h-4" />}
              {currentStep === (security ? STEPS.SELECT_ACCOUNT : STEPS.SELECT_SECURITY) ? 'Cancel' : 'Back'}
            </button>

            {currentStep < STEPS.REVIEW_CONFIRM ? (
              <button
                onClick={handleNext}
                disabled={!canProceedToNext()}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all ${
                  canProceedToNext()
                    ? 'bg-slate-900 text-white hover:bg-slate-800'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmitOrder}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Confirm Order
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
